import axiosConfig from './axiosConfig';

const buildRoleQuery = ({
  pageSize = 100,
  pageNumber = 1,
  personnelRoleType,
  status,
  searchKey,
  sort,
} = {}) => {
  const params = new URLSearchParams();
  params.set('pageSize', String(pageSize));
  params.set('pageNumber', String(pageNumber));
  if (sort) params.set('sort', JSON.stringify(sort));
  if (personnelRoleType) params.set('personnelRoleType', personnelRoleType);
  if (status) params.set('status', status);
  if (searchKey) params.set('searchKey', searchKey);
  return params.toString();
};

/** Admin — all roles (active + inactive). */
export const fetchAllRoles = async (options = {}) => {
  const query = buildRoleQuery(options);
  const res = await axiosConfig.get(`/role/all?${query}`);
  return res?.data?.data ?? { count: 0, rows: [] };
};

/** Admin — active roles only. */
export const fetchActiveRoles = async (options = {}) => {
  const query = buildRoleQuery({ status: 'active', ...options });
  const res = await axiosConfig.get(`/role?${query}`);
  return res?.data?.data ?? { count: 0, rows: [] };
};

/** Admin — inactive roles only (reactivate table). */
export const fetchInactiveRoles = async (options = {}) => {
  const query = buildRoleQuery({ status: 'inactive', ...options });
  const res = await axiosConfig.get(`/role?${query}`);
  return res?.data?.data ?? { count: 0, rows: [] };
};

export const createRole = async ({ name, personnelRoleType = null, status = 'active' }) => {
  const res = await axiosConfig.post('/role', { name, personnelRoleType, status });
  return res?.data?.data ?? null;
};

export const updateRole = async (id, rolePatch) => {
  const res = await axiosConfig.put(`/role/${id}`, { role: rolePatch });
  return res?.data?.data ?? null;
};

export const PERSONNEL_MODULE_OPTIONS = [
  { value: 'welding', label: 'Welding' },
  { value: 'coating', label: 'Coating' },
  { value: 'both', label: 'Both' },
];

/** Read personnel module type from API role objects (handles field/casing variants). */
export const getRoleModuleType = (roleOrValue) => {
  const raw =
    roleOrValue != null && typeof roleOrValue === 'object'
      ? roleOrValue.personnelRoleType ??
        roleOrValue.personnel_role_type ??
        roleOrValue.PersonnelRoleType
      : roleOrValue;
  if (raw == null || raw === '') return null;
  return String(raw).trim().toLowerCase();
};

/** Parse API role type into module tokens (e.g. `coating,welding` → welding + coating). */
export const parseRoleModuleTypes = (roleOrValue) => {
  const normalized = getRoleModuleType(roleOrValue);
  if (normalized == null) return new Set();
  if (normalized === 'both') return new Set(['welding', 'coating']);
  if (normalized.includes(',')) {
    return new Set(
      normalized
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
    );
  }
  return new Set([normalized]);
};

/** True when role applies to both DPR (welding) and DSR (coating). */
export const isBothModuleType = (roleOrValue) => {
  const normalized = getRoleModuleType(roleOrValue);
  if (normalized === 'both') return true;
  const types = parseRoleModuleTypes(roleOrValue);
  return types.has('welding') && types.has('coating');
};

/** Map API value to admin form select value. */
export const toFormPersonnelRoleType = (roleOrValue) => {
  if (isBothModuleType(roleOrValue)) return 'both';
  const normalized = getRoleModuleType(roleOrValue);
  if (normalized == null) return undefined;
  if (PERSONNEL_MODULE_OPTIONS.some((opt) => opt.value === normalized)) return normalized;
  return undefined;
};

export const formatPersonnelModuleLabel = (roleOrValue) => {
  if (isBothModuleType(roleOrValue)) return 'Both';
  const normalized = getRoleModuleType(roleOrValue);
  if (normalized == null) return '—';
  const match = PERSONNEL_MODULE_OPTIONS.find((opt) => opt.value === normalized);
  if (match) return match.label;
  return '—';
};

export const normalizePersonnelRoleType = (value) => {
  const normalized = getRoleModuleType(value);
  if (normalized == null) return null;
  if (normalized === 'both' || isBothModuleType(value)) return 'both';
  if (PERSONNEL_MODULE_OPTIONS.some((opt) => opt.value === normalized)) return normalized;
  return null;
};

/** Whether a role belongs in a DPR/DSR picker for the given module. */
export const roleMatchesPersonnelModule = (roleOrValue, personnelRoleType) => {
  const module = getRoleModuleType(personnelRoleType);
  if (module == null) return false;
  if (isBothModuleType(roleOrValue)) return true;
  const roleTypes = parseRoleModuleTypes(roleOrValue);
  if (module === 'both') {
    return roleTypes.has('welding') || roleTypes.has('coating');
  }
  return roleTypes.has(module);
};
